import { TabModel } from '../../common/types/TabModel';
import { IotaSessionsStore } from '../../common/stores';
import { SessionStoreBase } from '../../custom_shared/stores';
import { DataCrafterChildSession, DataCrafterRuntimeAppDto } from '../types';
import { observable, action, makeObservable, computed, runInAction } from 'mobx';
import DataCrafterService from '../services/DataCrafterService';
import { Utils } from '../../common/misc/Utils';
import { notification } from 'antd';
import { PageAction } from '../models';
import { SessionsVisualStore } from '../../sessions/stores';

enum Actions {
    PrepareOutput = 'PrepareOutput',
    PushToWorkflow = 'PushToWorkflow'
}

export default class SessionStore extends SessionStoreBase<DataCrafterRuntimeAppDto> {
    currentTemplateId: string | undefined = undefined;

    childSessions: DataCrafterChildSession[] = [];

    detachedWindow: boolean = false;

    isLoadingTemplate: boolean = false;

    constructor(
        tab: TabModel,
        iotaSessionsStore: IotaSessionsStore,
        private dataCrafterService: DataCrafterService,
        private sessionsVisualStore: SessionsVisualStore
    ) {
        super(tab, iotaSessionsStore);

        makeObservable(this, {
            currentTemplateId: observable,
            childSessions: observable,
            setCurrentTemplateId: action.bound,
            processedPackageId: computed,
            fetchOutputDocument: action.bound,
            sendOutput: action.bound,
            getChildSessions: action.bound,
            openChildSession: action.bound,
            getUserById: action.bound,
            analysisIsLocked: computed,
            workflowButtonTitle: computed,
            workflowButtonIsDisabled: computed,
            getTabJson: action.bound,
            detachedWindow: observable,
            setDetachedWindow: action.bound,
            isLoadingTemplate: observable,
            setIsLoadingTemplate: action.bound
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.iotaSessionsStore.iotaSessionChanges.subscribe((session: any) => {
            if (
                this.sessionId === session.sessionId ||
                this.childSessions.some(childSession => childSession.sessionId === session.sessionId)
            ) {
                this.getChildSessions();
            }
        });
    }

    get processedPackageId() {
        return this.response?.processedPackageId;
    }

    get analysisIsLocked() {
        return this.response?.analysisIsLocked;
    }

    get sessionId() {
        return this.tab.id;
    }

    setCurrentTemplateId(templateId: string | undefined, processTemplate: boolean = true) {
        this.currentTemplateId = templateId;

        if (templateId && processTemplate) {
            this.isLoadingTemplate = true;
            this.continueSession(Actions.PrepareOutput, {
                DataCrafterTemplate: { value: templateId }
            });
        }
    }

    setIsLoadingTemplate(isLoading: boolean) {
        this.isLoadingTemplate = isLoading;
    }

    async fetchOutputDocument() {
        if (!this.processedPackageId) {
            return;
        }

        try {
            const blob = await this.dataCrafterService.fetchOutputDocument(this.sessionId, this.processedPackageId);

            // Create a download link and trigger the download
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;

            const date = new Date();
            const formattedDate = Utils.formatDateShort(date).replace(',', '_').replace(/\s+/g, '');
            a.download = `${formattedDate}_DataCrafterOutput_${this.processedPackageId}.xlsx`;

            document.body.appendChild(a);
            a.click();

            // Cleanup
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Failed to fetch output document:', error);
            throw error;
        }
    }

    async sendOutput() {
        if (!this.processedPackageId) {
            return;
        }

        try {
            await this.dataCrafterService.sendOutput(this.sessionId, this.processedPackageId);
            notification.success({
                message: 'Document sent successfully'
            });
        } catch (error) {
            notification.error({
                message: 'An error has occurred while sending the output document'
            });
            console.error('Failed to send output document:', error);
        }
    }

    async analyzeDocument(applicationId: string) {
        if (!this.processedPackageId) {
            return;
        }

        const projectId = this.tab.metadata.projectId;

        if (!projectId) {
            notification.error({
                message: 'Project ID is not set. Try recreating the session.'
            });
            return;
        }

        try {
            await this.dataCrafterService.analyzeDocument(
                this.sessionId,
                this.processedPackageId,
                projectId,
                applicationId
            );
        } catch (error) {
            notification.error({
                message: 'An error has occurred while analyzing the document'
            });
            console.error('Failed to analyze document:', error);
        }
    }

    async getChildSessions() {
        if (!this.sessionId) {
            return;
        }

        try {
            const childSessions = await this.dataCrafterService.getChildSessions(this.sessionId);

            runInAction(() => {
                this.childSessions = childSessions;
            });
        } catch (error) {
            console.error('Failed to get child sessions:', error);
        }
    }

    async openChildSession(sessionId: string) {
        const session = await this.sessionsVisualStore.getSessionById(sessionId);
        const finishedStates = ['finished', 'aborted', 'archived', 'waitingaction'];

        if (session && finishedStates.includes(session.state.toLowerCase())) {
            this.sessionsVisualStore.switchToSession(session);
        }
    }

    getUserById(userId: string) {
        if (this.sessionsVisualStore?.allUsers[userId]) {
            return this.sessionsVisualStore.allUsers[userId];
        }

        return userId;
    }

    getTabJson() {
        return JSON.stringify(this.tab);
    }

    get workflowButtonTitle() {
        const pageAction = (this.response?.actions as PageAction[] | undefined)?.find(
            a => a.id === Actions.PushToWorkflow
        );
        return pageAction?.name ?? 'Send to Email';
    }

    get workflowButtonIsDisabled() {
        const pageAction = (this.response?.actions as PageAction[] | undefined)?.find(
            a => a.id === Actions.PushToWorkflow
        );
        return pageAction?.isDisabled ?? false;
    }

    setDetachedWindow(detachedWindow: boolean) {
        this.detachedWindow = detachedWindow;
    }
}
